// "Estimate from KNVB" (2026-10-24) — the Upload Zone's missing-GPS list for one tracking match,
// shown after a KNVB import and on demand from the Tracking Matches list. Two steps, never
// automatic: tick players → "Estimate from KNVB" shows a dry run (nothing written) → "Confirm and
// save" writes exactly those players. Staff/admin only for the actions (also enforced server-side
// from the session); everyone else just sees the list. All numbers come from the API.
import React, { useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle } from 'lucide-react';
import { getTrackingGpsGaps, estimateGpsFromKnvb, removeKnvbGpsEstimate } from '../../api';
import { EstimatedValue } from '../../components/EstimatedMarker';

const canEstimate = user => !!user && (['staff', 'admin'].includes(user.role) || user.isAdmin);
const btn = (primary, disabled) => ({
  padding: '7px 14px', borderRadius: 6, border: primary ? 'none' : '1px solid var(--border-strong)',
  background: primary ? '#3b82f6' : 'transparent', color: primary ? '#fff' : 'var(--text-secondary)',
  fontWeight: 600, fontSize: 12, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1, fontFamily: 'inherit',
});
const f0 = v => (v == null ? '—' : Math.round(v).toLocaleString());

export default function KnvbEstimatePanel({ trackingMatchId, initialGaps = null, user, onChanged }) {
  const [gaps, setGaps] = useState(initialGaps && !initialGaps.error ? initialGaps : null);
  const [error, setError] = useState(initialGaps?.error || '');
  const [selected, setSelected] = useState({});
  const [replaceRow, setReplaceRow] = useState({});
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(null);
  const staff = canEstimate(user);

  const reload = () => getTrackingGpsGaps(trackingMatchId)
    .then(g => { setGaps(g); setError(''); })
    .catch(e => setError(e.message));

  useEffect(() => { if (!gaps) reload(); }, [trackingMatchId]); // eslint-disable-line react-hooks/exhaustive-deps
  // Default: tick every missing player that isn't estimated yet or whose estimate is stale.
  useEffect(() => {
    if (!gaps?.missing) return;
    setSelected(Object.fromEntries(gaps.missing.map(m => [m.playerId, !m.estimated || !!m.estimate?.stale])));
  }, [gaps]);

  if (error) return <div style={{ marginTop: 10, fontSize: 13, color: '#fca5a5' }}><AlertTriangle size={14} /> {error}</div>;
  if (!gaps) return <div style={{ marginTop: 10, fontSize: 13, color: 'var(--text-muted)' }}>Checking GPS coverage…</div>;
  if (!gaps.linked || !gaps.gpsUploaded) return <div style={{ marginTop: 10, fontSize: 13, color: '#fcd34d' }}>{gaps.note}</div>;

  const ids = gaps.missing.filter(m => selected[m.playerId]).map(m => m.playerId);
  const replaceIds = gaps.missing.filter(m => m.blockedBy && replaceRow[m.playerId]).map(m => m.playerId);
  const blockedUnticked = gaps.missing.filter(m => selected[m.playerId] && m.blockedBy && !replaceRow[m.playerId]);

  const runPreview = async () => {
    setBusy(true); setSaved(null);
    try { setPreview(await estimateGpsFromKnvb(trackingMatchId, ids, { dryRun: true, replaceMeasuredDayRows: replaceIds })); setError(''); }
    catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };
  const confirm = async () => {
    setBusy(true);
    try {
      const res = await estimateGpsFromKnvb(trackingMatchId, preview.players.map(p => p.playerId), { replaceMeasuredDayRows: replaceIds });
      setSaved(res); setPreview(null); await reload(); onChanged && onChanged();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };
  const remove = async m => {
    if (!window.confirm(`Remove the KNVB estimate for ${m.playerName}?`)) return;
    setBusy(true);
    try { await removeKnvbGpsEstimate(trackingMatchId, m.playerId); await reload(); onChanged && onChanged(); }
    catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div style={{ marginTop: 12, padding: 12, borderRadius: 8, background: 'var(--bg-app)', border: '1px solid var(--border-strong)', color: 'var(--text-primary)', fontSize: 13 }}>
      <div style={{ fontWeight: 600, marginBottom: 6 }}>{gaps.summary}</div>
      {gaps.unmatched?.length > 0 && (
        <div style={{ fontSize: 12, color: '#fcd34d', marginBottom: 6 }}>
          <AlertTriangle size={12} /> Not matched to a player (not estimable until an alias exists): {gaps.unmatched.map(u => `${u.trackingName}${u.candidates?.length ? ` (${u.candidates.join(' / ')}?)` : ''}`).join(', ')}
        </div>
      )}
      {gaps.missing.map(m => (
        <div key={m.playerId} style={{ padding: '6px 0', borderTop: '1px solid var(--border)' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: staff ? 'pointer' : 'default' }}>
            {staff && <input type="checkbox" checked={!!selected[m.playerId]} onChange={e => setSelected(s => ({ ...s, [m.playerId]: e.target.checked }))} />}
            <span style={{ fontWeight: 600 }}>{m.playerName}</span>
            <span style={{ color: 'var(--text-muted)' }}>{m.position || '—'} · {m.minutes} min (KNVB)</span>
            {m.estimated && (
              <span style={{ fontSize: 11, color: m.estimate?.stale ? '#fcd34d' : '#6ee7b7' }}>
                {m.estimate?.stale ? `estimated (${m.estimate.modelVersion}) — out of date, re-estimate` : `estimated (${m.estimate?.modelVersion})`}
              </span>
            )}
            {m.estimated && staff && <button onClick={() => remove(m)} disabled={busy} style={{ ...btn(false, busy), padding: '2px 8px', marginLeft: 'auto' }}>Remove estimate</button>}
          </label>
          {m.blockedBy && (
            <div style={{ marginLeft: staff ? 24 : 0, fontSize: 12, color: '#fcd34d' }}>
              Has a measured GPS row without match halves: {f0(m.measuredDayRow?.td)} m TD, {f0(m.measuredDayRow?.hmld)} m HMLD in {f0(m.measuredDayRow?.minutes)} min, max {m.measuredDayRow?.maxSpeed ?? '—'} km/h — looks like a unit that was on but not worn.
              {staff && (
                <label style={{ display: 'block', marginTop: 2, color: 'var(--text-secondary)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!replaceRow[m.playerId]} onChange={e => setReplaceRow(s => ({ ...s, [m.playerId]: e.target.checked }))} /> Replace that row with the estimate (kept, and restored if the estimate is removed)
                </label>
              )}
            </div>
          )}
        </div>
      ))}

      {staff ? (
        <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <button onClick={runPreview} disabled={busy || !ids.length} style={btn(true, busy || !ids.length)}>Estimate from KNVB</button>
          {blockedUnticked.length > 0 && <span style={{ fontSize: 11, color: '#fcd34d' }}>{blockedUnticked.map(m => m.playerName).join(', ')}: tick "replace that row", or {blockedUnticked.length === 1 ? 'he is' : 'they are'} skipped.</span>}
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Shows the numbers first — nothing is saved until you confirm.</span>
        </div>
      ) : gaps.missing.length > 0 && (
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8 }}>Only staff or admin can estimate.</div>
      )}

      {preview && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>Preview — not saved</div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 6 }}>
            Factors (Σ GPS ÷ Σ KNVB over {preview.factors?.td?.n} measured players): TD {preview.factors?.td?.factor?.toFixed(3)} · HID {preview.factors?.hid?.factor?.toFixed(3)} · SD {preview.factors?.sd?.factor?.toFixed(3)} ({preview.factors?.td?.source})
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead><tr style={{ color: 'var(--text-muted)', textAlign: 'right' }}>
              <th style={{ textAlign: 'left' }}>Player</th><th>Min</th><th>TD</th><th>HID</th><th>SD</th><th>HMLD</th><th>HMLD/min</th><th>Max spd</th>
            </tr></thead>
            <tbody>
              {preview.players.map(p => (
                <tr key={p.playerId} style={{ textAlign: 'right', borderTop: '1px solid var(--border)' }}>
                  <td style={{ textAlign: 'left' }}>{p.playerName}{p.replacesMeasuredDayRow ? ' (replaces measured row)' : ''}</td>
                  <td>{p.minutes.toFixed(1)}</td>
                  {['td', 'hid', 'sd', 'hmld'].map(k => <td key={k}><EstimatedValue value={p.values[k]} basis={p.basis} metric={k} /></td>)}
                  <td>{p.basis.metrics.hmld.per_minute?.toFixed(1) ?? '—'}</td>
                  <td><EstimatedValue value={p.values.max_speed} basis={p.basis} metric="max_speed" format={v => v.toFixed(1)} /></td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>Not estimated (stay empty): RD, SA, Explosive, sprint counts, heart rate. Hover TD/HID for their interval.</div>
          {preview.errors?.length > 0 && (
            <div style={{ fontSize: 12, color: '#fca5a5', marginTop: 6 }}>{preview.errors.map(e => e.message).join(' · ')}</div>
          )}
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button onClick={confirm} disabled={busy || !preview.players.length} style={btn(true, busy || !preview.players.length)}>Confirm and save {preview.players.length} estimate{preview.players.length === 1 ? '' : 's'}</button>
            <button onClick={() => setPreview(null)} disabled={busy} style={btn(false, busy)}>Cancel</button>
          </div>
        </div>
      )}
      {saved && (
        <div style={{ marginTop: 10, fontSize: 12, color: '#6ee7b7' }}>
          <CheckCircle size={12} /> Saved {saved.written.length} estimate{saved.written.length === 1 ? '' : 's'}{saved.skipped?.length ? ` · skipped: ${saved.skipped.map(s => `${s.playerId} (${s.error})`).join(', ')}` : ''}.
        </div>
      )}
    </div>
  );
}
